// src/lib/appearance/options.ts
//
// Appearance options — decorations and backgrounds read live from
// public.reference_items (lists appearance_decorations · appearance_backgrounds).
// Spec: docs/specs/appearance-options-tables-spec.md §3.1–3.2.
//
// Security rule (§3.2): meta holds numbers and enum names only — never CSS,
// never a URL. Every editor of these lists is any signed-in user, so this file
// is the second fence after the DB CHECK (public.appearance_meta_valid,
// migration 20261003200501). The two must stay in lockstep: the validator here
// mirrors the SQL function key for key and range for range.
//
// Pure module: no React, no Supabase. Safe to import from gam-tokens.ts.

export const DECORATIONS_LIST = 'appearance_decorations'
export const BACKGROUNDS_LIST = 'appearance_backgrounds'

export type BlurMode = 'chrome' | 'none'
export type MotionMode = 'calm' | 'snappy' | 'bouncy'
export type DecorationScope = 'system' | 'widget'

export interface DecorationMeta {
  spine: number
  slash: number
  arch: number
  shimmer: number
  blur: BlurMode
  motion: MotionMode
  scope: DecorationScope[]
}

export type BackgroundKind = 'none' | 'grid' | 'dots' | 'glow'
export type BackgroundTone = 'line' | 'accent'

export interface BackgroundMeta {
  kind: BackgroundKind
  size: number
  major: number
  opacity: number
  tone: BackgroundTone
}

/** Fallback when a row is missing or invalid (acceptance criterion 7). */
export const NONE_DECORATION: DecorationMeta = {
  spine: 0,
  slash: 0,
  arch: 0,
  shimmer: 0,
  blur: 'none',
  motion: 'calm',
  scope: ['system', 'widget'],
}

export const NONE_BACKGROUND: BackgroundMeta = {
  kind: 'none',
  size: 0,
  major: 0,
  opacity: 0,
  tone: 'line',
}

const DECORATION_KEYS = ['arch', 'blur', 'motion', 'scope', 'shimmer', 'slash', 'spine']
const BACKGROUND_KEYS = ['kind', 'major', 'opacity', 'size', 'tone']

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function hasExactKeys(o: Record<string, unknown>, keys: string[]): boolean {
  const actual = Object.keys(o).sort()
  return actual.length === keys.length && actual.every((k, i) => k === keys[i])
}

function inRange(v: unknown, min: number, max: number): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[]): v is T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v)
}

/** Mirror of the decoration branch of public.appearance_meta_valid. */
export function parseDecorationMeta(meta: unknown): DecorationMeta | null {
  if (!isPlainObject(meta) || !hasExactKeys(meta, DECORATION_KEYS)) return null
  const { spine, slash, arch, shimmer, blur, motion, scope } = meta
  if (!inRange(spine, 0, 1) || !inRange(slash, 0, 1) || !inRange(arch, 0, 1) || !inRange(shimmer, 0, 1)) return null
  if (!oneOf(blur, ['chrome', 'none'] as const)) return null
  if (!oneOf(motion, ['calm', 'snappy', 'bouncy'] as const)) return null
  if (!Array.isArray(scope) || scope.length === 0) return null
  if (!scope.every((s) => oneOf(s, ['system', 'widget'] as const))) return null
  return { spine, slash, arch, shimmer, blur, motion, scope: [...scope] as DecorationScope[] }
}

/** Mirror of the background branch of public.appearance_meta_valid. */
export function parseBackgroundMeta(meta: unknown): BackgroundMeta | null {
  if (!isPlainObject(meta) || !hasExactKeys(meta, BACKGROUND_KEYS)) return null
  const { kind, size, major, opacity, tone } = meta
  if (!oneOf(kind, ['none', 'grid', 'dots', 'glow'] as const)) return null
  if (!oneOf(tone, ['line', 'accent'] as const)) return null
  if (!inRange(size, 0, 200) || !inRange(major, 0, 1000) || !inRange(opacity, 0, 0.3)) return null
  return { kind, size, major, opacity, tone }
}

// ─── Decoration → CSS variables ──────────────────────────────────────────
// The code owns every CSS string. The row only chooses between them and
// supplies validated numbers.

const SPRING: Record<MotionMode, { default: string; bounce: string }> = {
  calm: { default: 'cubic-bezier(0.32, 0.72, 0, 1)', bounce: 'cubic-bezier(0.32, 0.72, 0, 1)' },
  snappy: { default: 'cubic-bezier(0.4, 0, 0.2, 1)', bounce: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  bouncy: { default: 'cubic-bezier(0.32, 0.72, 0, 1)', bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
}

/**
 * The CSS variables a decoration writes. `blur: "chrome"` leaves
 * --gam-blur-chrome to the active skin (each skin has its own strength);
 * `blur: "none"` turns the frosted glass off.
 */
export function decorationCssVars(d: DecorationMeta): Record<string, string> {
  const vars: Record<string, string> = {
    '--gam-decoration-spine': String(d.spine),
    '--gam-decoration-slash': String(d.slash),
    '--gam-decoration-arch': String(d.arch),
    '--gam-hero-shimmer': String(d.shimmer),
    '--gam-spring-default': SPRING[d.motion].default,
    '--gam-spring-bounce': SPRING[d.motion].bounce,
  }
  if (d.blur === 'none') vars['--gam-blur-chrome'] = 'none'
  return vars
}

// ─── System-level choice (stage 1: local only, spec §3.3) ────────────────

export const DECORATION_STORAGE_KEY = 'cc-decoration'

interface StoredDecoration {
  key: string
  meta: DecorationMeta
}

/**
 * The stored system decoration, re-validated on every read — localStorage is
 * not trusted more than the database. null = follow the skin (the default,
 * so nothing changes for anyone who never opens the picker).
 */
export function readStoredDecoration(): StoredDecoration | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(DECORATION_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isPlainObject(parsed) || typeof parsed.key !== 'string') return null
    const meta = parseDecorationMeta(parsed.meta)
    return meta ? { key: parsed.key, meta } : null
  } catch {
    return null
  }
}

export function writeStoredDecoration(value: StoredDecoration | null): void {
  if (typeof window === 'undefined') return
  try {
    if (value) localStorage.setItem(DECORATION_STORAGE_KEY, JSON.stringify(value))
    else localStorage.removeItem(DECORATION_STORAGE_KEY)
  } catch {
    /* localStorage failure is non-fatal */
  }
}

/** Writes the stored decoration over whatever the skin just set. No-op when none is stored. */
export function applyStoredDecoration(): void {
  if (typeof document === 'undefined') return
  const stored = readStoredDecoration()
  if (!stored) return
  const style = document.documentElement.style
  for (const [k, v] of Object.entries(decorationCssVars(stored.meta))) style.setProperty(k, v)
}
